import { HttpContext, httpResource } from "@angular/common/http";
import { computed, Injectable } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { Observable } from "rxjs";
import { distinctUntilChanged, shareReplay, startWith } from "rxjs/operators";
import { getUserBenefits } from "src/app/api/smart-pensjon-rest-api/functions";
import { UserBenefitsRepresentation } from "src/app/api/smart-pensjon-rest-api/models";
import { SKIP_HTTP_CACHE } from "src/app/interceptors/caching.interceptor";
import { EXPECTED_401 } from "src/app/interceptors/keycloak-bearer.interceptor";
import { buildRestApiRootUrl } from "src/app/utils/rest-api-root-url";
import { operations } from "./benefits-rest-api.types";

export type CustomerProgramsResponse =
  operations["getBenefitProgramsForCustomer"]["responses"]["200"]["content"]["*/*"];

/**
 * Whether the customer has benefit programs. The backend decides it in `GET /user/benefits`: it calls
 * the benefits API, skips the call for an advisor acting on the customer's behalf, and answers 500 when
 * the call fails.
 */
@Injectable({
  providedIn: "root",
})
export class BenefitsService {
  private readonly restApiRootUrl: string = buildRestApiRootUrl();

  /**
   * Sent when the first class that injects this service creates it.
   * Below auth level 3 the backend answers 401. That is expected, so it is not reported to Monitoring.
   * SKIP_HTTP_CACHE keeps the CachingInterceptor out, so {@link fetchCustomerBenefits} always hits the network.
   */
  private readonly benefits = httpResource<UserBenefitsRepresentation>(
    () => ({
      url: this.restApiRootUrl + getUserBenefits.PATH,
      context: new HttpContext().set(EXPECTED_401, true).set(SKIP_HTTP_CACHE, true),
    }),
    {
      parse: (raw: unknown): UserBenefitsRepresentation => (raw as { payload: UserBenefitsRepresentation }).payload,
    },
  );

  // value() throws in the error state, so check hasValue() first. Not loaded, a failed request, `null`
  // and a missing field all count as false.
  private readonly hasBenefits = computed(
    (): boolean => this.benefits.hasValue() && this.benefits.value()?.hasBenefits === true,
  );

  /**
   * Emits false at once, so the readers can render the rest right away, and true once
   * `GET /user/benefits` answers with `hasBenefits: true`. A late subscriber gets the latest value.
   */
  public readonly hasBenefits$: Observable<boolean> = toObservable(this.hasBenefits).pipe(
    startWith(false),
    distinctUntilChanged(),
    shareReplay(1),
  );

  /**
   * Used by the dev panel. Sends `GET /user/benefits` again. hasBenefits$ keeps its value while the
   * reload runs, then follows the new answer. Does nothing while the first request is still running.
   */
  public fetchCustomerBenefits(): void {
    this.benefits.reload();
  }
}
